// src/lib/prepaySentence.js — the PURE half of the contract composer's prepayment list
// (owner rulings 2026-09-10, migration 1048): no React, no i18n, so `node --test` can run it.
//
// Ruling 1 — prepayment is a LIST, not checkboxes. One template field `prepayments` of kind
// `prepay_list`, whose value is
//   [ {month:'1', full:true},
//     {month:'2', full:false, paid:{value:300,currency:'USD'}, remaining:{value:300,currency:'USD'}, due:'2026-11-15'},
//     {month:'last', full:true} ]
// `month` is '1'…'12' or 'last'. A FULL month is paid at the rent amount — the row itself
// carries no figure, so the sentence needs `rent` (the form's own money value) passed in.
//
// THE DEPOSIT IS NOT A ROW (owner 2026-09-11: "We should leave a box and remove deposit
// selection. Writing in means there is deposit paid."). The deposit money field alone is the
// fact: when it carries an amount, the sentence adds "დეპოზიტი — <amount> (სრულად)" and the
// total includes it, automatically. The month select never offers it, so "+ add a month" is
// no longer misleading, and "დეპოზიტი — — (სრულად)" (a deposit row with no amount) can no
// longer be produced. An OLDER stored list that carries a {month:'deposit'} row still renders
// (nothing stored is rewritten); the server (lib/contract_render.js prepaySummary) applies the
// same rule, so the preview and the document agree.
//
// Ruling 2 — the constructed sentence ("paid x, left to pay y till date"). `prepaySentence`
// MIRRORS THE SERVER'S wording, which is what the contract text will carry, so the agent reads
// under the rows exactly what the party will read in clause 3. The three wordings are pinned by
// src/lib/prepaySentence.test.mjs; change them there and here together, or the preview lies.
//   • Georgian ordinals: 1 → 1-ლი, 2–20 → მე-N, 21+ → N-ე.
//   • Dates dd.mm.yyyy; numbers with en-US thousands separators.
//   • The total is printed only when EVERY paid amount shares one currency (and every amount is
//     known) — summing USD and GEL would invent a rate, the same rule as the binder (0434).
//
// Ruling 3 — field conditions. `fieldCondition` evaluates a draft field's `condition` against the
// current values with the server's semantics: null → show; `key` → truthy (a money object is
// truthy only when its value is a NUMBER — `{value:null}` is an unanswered field, not an answer);
// `!key` → falsy; `key=value` → string equality. Example the owner gave: "კომუნალურის ლიმიტი
// ქირაში exists only if utilities are included in rent".

import { addPeriod, diffPeriod } from './leasePeriod.js'

export const PREPAY_MONTHS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', 'last']
export const PREPAY_LANGS = ['ka', 'en', 'ru']

// ⚠️ „პირველი", not „1-ლი" (owner 2026-09-12, reading a filled contract: "better formal
// Georgian"). The digit-plus-suffix form is how people write a date in a message; a contract
// spells the first one out. Only 1 changes — „მე-2 თვე" is already the formal written form.
export function ordinalKa(n) {
  n = Number(n)
  if (n === 1) return 'პირველი'
  if (n >= 2 && n <= 20) return `მე-${n}`
  return `${n}-ე`
}
export function ordinalEn(n) {
  n = Number(n)
  const mod100 = n % 100
  const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : n % 10 === 1 ? 'st' : n % 10 === 2 ? 'nd' : n % 10 === 3 ? 'rd' : 'th'
  return `${n}${suffix}`
}
// месяц is masculine, so every ordinal takes -й (1-й, 2-й, 3-й, 11-й, 21-й).
export function ordinalRu(n) { return `${Number(n)}-й` }

const WORDS = {
  ka: { month: 'თვე', last: 'ბოლო თვე', deposit: 'უზრუნველყოფის თანხა', full: 'სრულად', total: 'სულ გადახდილია ხელმოწერისას', binder: 'ბე', binderPaid: 'ადრე გადახდილი, აკლდება', dueNow: 'სულ გადასახდელია ხელმოწერისას' },
  en: { month: 'month', last: 'last month', deposit: 'security deposit', full: 'paid in full', total: 'total paid at signing', binder: 'binder', binderPaid: 'paid earlier, deducted', dueNow: 'total due at signing' },
  ru: { month: 'месяц', last: 'последний месяц', deposit: 'гарантийный депозит', full: 'оплачен полностью', total: 'итого оплачено при подписании', binder: 'задаток', binderPaid: 'внесён ранее, вычитается', dueNow: 'итого к оплате при подписании' },
}
const ORD = { ka: ordinalKa, en: ordinalEn, ru: ordinalRu }

export const normLang = lang => (PREPAY_LANGS.includes(lang) ? lang : 'ka')

// "1-ლი თვე" / "2nd month" / "последний месяц" — also the label of the row's month select.
// `periodMonths` (owner ruling 2026-09-11, phone testing) is a UI-ONLY extra: when the lease
// period is known, the last month's label in the SELECT names its ordinal — "ბოლო თვე (მე-12)"
// — while its VALUE stays 'last' (the server understands 'last'). The contract SENTENCE never
// passes it, so clause 3 keeps "ბოლო თვე" unchanged: the server renders the document.
export function monthLabel(month, lang, periodMonths) {
  const l = normLang(lang), w = WORDS[l]
  if (month === 'last') {
    const n = Number(periodMonths)
    return Number.isInteger(n) && n >= 1 ? `${w.last} (${ORD[l](n)})` : w.last
  }
  if (month === 'deposit') return w.deposit
  const n = parseInt(month, 10)
  if (!Number.isFinite(n)) return String(month ?? '')
  return `${ORD[l](n)} ${w.month}`
}

// ── The rental PERIOD (owner rulings 2026-09-11) ───────────────────────────────────────────
// "Rental period drives the end date and the month list." The calendar rule lives in ONE
// place — src/lib/leasePeriod.js (the linked start · duration · end control, later the same
// day) — and these two are its month-only faces, kept for the callers and tests that use
// them: the whole months of the current interval drive the prepayment month list.

// The inclusive end of a lease of `n` months from `startIso` — leasePeriod's `addPeriod` in
// months: 2026-10-15 + 12 → 2027-10-14, 2026-01-31 + 1 → 2026-02-27. Unparseable → ''.
export const leaseEndFrom = (startIso, n) => addPeriod(startIso, n, 'month')

// The whole number of months between a lease start and its inclusive end, or null when the
// pair is not exactly `leaseEndFrom(start, n)` for some n — `diffPeriod` when it is exact.
export function leasePeriodMonths(startIso, endIso) {
  const d = diffPeriod(startIso, endIso)
  return d && d.exactMonths ? d.months : null
}

// The month values the prepayment select offers for a period of `n` months: 1 … n−1, then
// ONE row for the last month ('last' — never both "მე-12 თვე" and "ბოლო თვე" on a 12-month
// lease). No period known (or under one month) → today's full list. Never the deposit.
// The composer passes the WHOLE months of the interval (floor), so a hand-typed end of
// "6 თვე და 12 დღე" still lists six months.
export function prepayMonthOptions(periodMonths) {
  const n = Number(periodMonths)
  if (!Number.isInteger(n) || n < 1) return PREPAY_MONTHS
  const out = []
  for (let i = 1; i < n; i++) out.push(String(i))
  out.push('last')
  return out
}

// ISO yyyy-mm-dd → dd.mm.yyyy; anything else is passed through untouched.
export function fmtDate(iso) {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso || '')
}

// en-US thousands separators, up to two decimals, no trailing zeros: 2100 → "2,100",
// 1234.5 → "1,234.5". Done by hand so the output does not depend on the runtime's ICU.
export function fmtNum(n) {
  const v = Number(n)
  if (!Number.isFinite(v)) return ''
  const neg = v < 0 ? '-' : ''
  const abs = Math.round(Math.abs(v) * 100) / 100
  const [int, dec] = String(abs).split('.')
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${neg}${grouped}${dec ? `.${dec}` : ''}`
}

export const isMoney = m => m && typeof m === 'object' && typeof m.value === 'number' && Number.isFinite(m.value)
export const moneyText = m => (isMoney(m) ? `${fmtNum(m.value)} ${m.currency || ''}`.trim() : '')

// The amount a row is PAID at signing: the rent for a full month, the deposit for a full
// deposit, the `paid` figure for a partial row. Null when the figure is not known.
export function paidAmountOf(row, rent, deposit) {
  if (!row) return null
  if (row.full !== false) {
    const src = row.month === 'deposit' ? deposit : rent
    return isMoney(src) ? { value: src.value, currency: src.currency || '' } : null
  }
  return isMoney(row.paid) ? { value: row.paid.value, currency: row.paid.currency || '' } : null
}

// The sentence, in one language. Empty list → ''.
// `planned` (the advance, 2026-09-23): the MAIN agreement's future payments — no "paid in full", no
// "total paid at signing"; mirrors lib/contract_render.js prepaySummary(…, planned)
export function prepaySentence(list, lang, rent, deposit, binder, planned = false) {
  let rows = Array.isArray(list) ? list.filter(r => r && typeof r === 'object') : []
  // The deposit box is the fact: an amount in it is a deposit paid at signing (header).
  if (isMoney(deposit) && deposit.value > 0 && !rows.some(r => r.month === 'deposit')) rows.push({ month: 'deposit', full: true })
  // IN MONTH ORDER — 1, 2, …, last, deposit; stable (mirrors lib/contract_render.js prepaySummary, 2026-09-17)
  const rank = r => { const m = r && r.month; if (m === 'deposit') return 1e6 + 1; if (m === 'last') return 1e6; const n = Number(m); return Number.isFinite(n) ? n : 1e6 - 1 }
  rows = rows.map((r, i) => [r, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(x => x[0])
  // THE BINDER COMES FIRST (owner 2026-09-11) — paid days earlier, deducted from what is handed
  // over at signing. Mirrors lib/contract_render.js prepaySummary; never subtracted across
  // currencies (that would invent a rate).
  const hasBinder = isMoney(binder) && binder.value > 0
  if (rows.length === 0 && !hasBinder) return ''
  const l = normLang(lang), w = WORDS[l]
  const parts = []
  const paid = []
  for (const r of rows) {
    const label = monthLabel(r.month, l)
    const amt = paidAmountOf(r, rent, deposit)
    paid.push(amt)
    if (r.full !== false) {
      parts.push(planned ? `${label} — ${amt ? moneyText(amt) : '—'}` : `${label} — ${amt ? moneyText(amt) : '—'} (${w.full})`)
      continue
    }
    const p = amt ? moneyText(amt) : '—'
    const rem = isMoney(r.remaining) ? moneyText(r.remaining) : '—'
    const due = r.due ? fmtDate(r.due) : ''
    if (planned) {
      if (isMoney(r.remaining)) paid.push(r.remaining)
      const then = isMoney(r.remaining) ? (l === 'ka' ? `, შემდეგ ${rem}${due ? ` არაუგვიანეს ${due}-ისა` : ''}` : l === 'ru' ? `, затем ${rem}${due ? ` до ${due}` : ''}` : `, then ${rem}${due ? ` by ${due}` : ''}`) : ''
      parts.push(`${label} — ${p}${then}`)
      continue
    }
    // ⚠️ KEEP IN STEP WITH lib/contract_render.js prepaySummary — the agent reads this while
    // filling and the signer reads that one on the paper; two wordings for one fact is how a
    // form and a contract start disagreeing. Owner's phrasing, 2026-09-12.
    if (l === 'ka') {
      const from = r.month === 'deposit' ? 'უზრუნველყოფის თანხიდან'
        : `${r.month === 'last' ? 'ბოლო თვის' : `${ordinalKa(r.month)} თვის`} ქირიდან`
      parts.push(`${from} გადახდილია ${p}, დარჩენილი ${rem}${due ? ` გადასახდელია არაუგვიანეს ${due}-ისა` : ''}`)
    }
    else if (l === 'ru') parts.push(`${label} — оплачено ${p}, ${rem} остаток${due ? ` до ${due}` : ''}`)
    else parts.push(`${label} — paid ${p}, ${rem} left to pay${due ? ` by ${due}` : ''}`)
  }
  if (hasBinder) parts.push(`${w.binder} — ${moneyText(binder)} (${w.binderPaid})`)
  // The total: only when every amount is known and they all share one currency (the binder's
  // included, or it is listed without a total).
  const all = hasBinder ? [...paid, { value: -binder.value, currency: binder.currency }] : paid
  if (all.every(Boolean) && all.length && new Set(all.map(a => a.currency)).size === 1) {
    const sum = all.reduce((a, b) => a + b.value, 0)
    // after a binder the "due at signing" line always prints, floored at zero (mirrors the server)
    const label = planned ? (hasBinder ? ({ ka: 'სულ, ბეს გამოკლებით', en: 'total after deducting the binder', ru: 'итого за вычетом задатка' })[l] : ({ ka: 'სულ', en: 'total', ru: 'итого' })[l])
      : hasBinder ? w.dueNow : w.total
    if (hasBinder || sum > 0) parts.push(`${label}: ${fmtNum(hasBinder ? Math.max(0, Math.round(sum * 100) / 100) : sum)} ${all[0].currency}`.trim())
  }
  return parts.join('; ')
}

// ── Ruling 3: a field's `condition` against the current values ─────────────────────────────
// 🔴 A PORT, NOT A PARAPHRASE (2026-09-17). This used to call a money `{value: 0}` present and the
//    string "false" present, while the server (lib/contract_render.js truthy) treats 0, "false" and
//    `false` as ABSENT. So a deposit of 0 asked for deposit_notice_days and a retention of 0 asked for
//    payer_fault_basis — questions the paper never prints. Line for line with the server now; the
//    cases are pinned by prepaySentence.test.mjs.
export function truthy(v) {
  if (Array.isArray(v)) return v.length > 0
  if (v && typeof v === 'object') {
    if ('value' in v) return truthy(v.value)        // money: {value, currency}
    if ('party_id' in v) return !!v.party_id        // a chosen person: {party_id, name}
    return Object.keys(v).length > 0
  }
  return !(v == null || v === '' || v === false || v === 'false' || v === 0)
}
// lib/prior_payment.js normalizePriorPayment — the server evaluates every condition on these values.
const normalizePrior = v => (v && String(v.prior_payment_kind || '') === 'prepaid_commission' && v.prior_payment_credited_against !== 'agency_commission'
  ? { ...v, prior_payment_credited_against: 'agency_commission' } : v)
// `langs` only answers the 'bilingual' condition; the server's missing-field gate (core_contract.js
// activeFields) evaluates with ['ka','en'], so that is the default here.
export function fieldCondition(cond, values, langs = ['ka', 'en']) {
  if (!cond) return true
  const c = String(cond)
  const v = normalizePrior(values || {})
  if (c === 'bilingual') return (langs || []).length > 1
  if (c.startsWith('!')) return !truthy(v[c.slice(1)])
  const eq = c.indexOf('=')
  if (eq > 0) {
    const key = c.slice(0, eq), want = c.slice(eq + 1)
    const s = String(v[key] ?? '')
    // "key=a|b" — one of several values. ⚠️ The MIRROR of contract_render.js's conditionHolds;
    // this one decides which questions the form asks, that one which clauses print, and a
    // condition only half of them understands asks nothing and prints something.
    return want.includes('|') ? want.split('|').some(w => w.trim() === s) : s === want
  }
  return truthy(v[c])
}
// The values with every hidden answer dropped — lib/contract_render.js visibleValues, to a fixpoint
// (hiding one field can hide another whose condition names it). A stale lease_start left behind when
// the purpose became a purchase is not an answer and may not contradict anything.
export function visibleValues(fields, values, langs = ['ka', 'en']) {
  const out = { ...(values || {}) }
  const conditional = (fields || []).filter(f => f && f.key && f.condition)
  for (let pass = 0; pass <= conditional.length; pass++) {
    let changed = false
    for (const f of conditional) {
      if (!(f.key in out)) continue
      if (!fieldCondition(f.condition, out, langs)) { delete out[f.key]; changed = true }
    }
    if (!changed) break
  }
  return out
}
